import { Box, Hide, Stack } from '@chakra-ui/react';
import { Grid } from '@time-line/components/common';
import { PromoCode } from 'components/common/PromoCode';
import {
  ProductBulletPoints,
  RichTextResolver,
} from 'components/common/RichTextResolver';
import { ProductCheckmarks } from 'components/product/ProductCheckmarks';
import { Unavailable } from 'components/product/ProductConfiguratorRender/Unavailable';
import { ProductCtas } from 'components/product/ProductCtas';
import { ProductDisclaimer } from 'components/product/ProductDisclaimer';
import { ProductGalleryDesktop } from 'components/product/ProductGallery/ProductGalleryDesktop';
import { ProductGalleryMobile } from 'components/product/ProductGallery/ProductGalleryMobile';
import { ProductIntro } from 'components/product/ProductIntro';
import { ProductSeeItInAction } from 'components/product/ProductSeeItInAction';
import { SkincareFormatRadioGroup } from 'components/product/ProductVariationSelector/SkincareFormatRadioGroup';
import { useFullShopifyProducts } from 'hooks/product/useFullShopifyProducts';
import { useProductsUpdatedPrices } from 'hooks/product/useProductsUpdatedPrices';
import { useSanityProduct } from 'hooks/product/useSanityProduct';
import { useState } from 'react';
import type { SkincareFormatPicker as SkincareFormatPickerProps } from 'types/cms/blocks';
import { PurchaseType } from 'types/product/PurchaseType';
import type { ShopifyProduct } from 'types/product/ShopifyProduct';
import { resolveProductInfo } from 'utils/product/resolveProductInfo';

export const SkincareFormatPicker = ({
  productFormats,
  ...props
}: SkincareFormatPickerProps) =>
  productFormats.length > 0 ? (
    <SkincareFormatPickerContent productFormats={productFormats} {...props} />
  ) : null;

const SkincareFormatPickerContent = ({
  _key,
  productFormats,
  promoCodes,
  isPromoCodeVisible,
  isDiscountHidden,
  ctaType,
  amazonButton,
}: SkincareFormatPickerProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const selectedFormat = productFormats[selectedIndex];
  const { slug, media, bulletPoints, seeItInAction, disclaimer } =
    selectedFormat.product;

  const sanityProduct = useSanityProduct(slug, { required: true });

  const { data: shopifyProducts, isLoading: isProductsLoading } =
    useFullShopifyProducts(productFormats.map((format) => format.product.slug));

  const { data: updatedProducts, isLoading: isUpdatedProductsLoading } =
    useProductsUpdatedPrices({
      blockId: _key,
      shopifyProducts: isProductsLoading
        ? null
        : shopifyProducts.filter((item): item is ShopifyProduct =>
            Boolean(item),
          ),
      promoCodes,
    });

  const selectedShopifyProduct = updatedProducts?.find(
    (item) => item?.id === shopifyProducts[selectedIndex]?.id,
  );
  const selectedVariant = selectedShopifyProduct?.variants.find(
    (variant) => variant.id === selectedFormat.variant.gid,
  );

  const productInfo = resolveProductInfo({
    sanityProduct,
    images: media,
  });

  const isLoading = isProductsLoading || isUpdatedProductsLoading;
  const isSelectedAvailable = !!selectedVariant?.availableForSale;

  const renderConfigurator = () => {
    if (isLoading) return null;

    return (
      <Stack spacing="2xs">
        <SkincareFormatRadioGroup
          productFormats={productFormats}
          shopifyProducts={updatedProducts ?? []}
          selectedSlug={slug}
          onSelect={(optionSlug) =>
            setSelectedIndex(
              productFormats.findIndex(
                (format) => format.product.slug === optionSlug,
              ),
            )
          }
          isDiscountHidden={isDiscountHidden}
        />
        {selectedVariant ? (
          <ProductCtas
            variant={selectedVariant}
            otpVariant={selectedVariant}
            isPreorder={sanityProduct.isPreorder}
            ctaType={ctaType}
            purchaseType={PurchaseType.OTP}
            amazonButton={amazonButton}
            asin={sanityProduct.asin}
            isDiscountHidden={isDiscountHidden}
            promoCodes={promoCodes}
          />
        ) : (
          <Unavailable />
        )}
      </Stack>
    );
  };

  return (
    <Grid id={_key} className="section--skincare-format-picker" my="2xl">
      {productInfo.images.length > 0 && (
        <Hide above="lg">
          <Box
            className="product_media"
            gridColumn="span 12"
            display={{ base: 'block', lg: 'none' }}
          >
            <ProductGalleryMobile
              key={slug}
              media={productInfo.images}
              title={productInfo.title}
              isDoctorRecommended={sanityProduct.isDoctorRecommended}
              isLoading={isProductsLoading}
            />
          </Box>
        </Hide>
      )}
      <Grid.Inner gridRowGap={{ base: 'm', md: 'lg' }}>
        {productInfo.images.length > 0 && (
          <Hide below="lg">
            <Box
              className="product_media"
              gridColumn="span 8"
              mr="5%"
              display={{ base: 'none', lg: 'block' }}
            >
              <ProductGalleryDesktop
                key={slug}
                media={productInfo.images}
                title={productInfo.title}
                isDoctorRecommended={sanityProduct.isDoctorRecommended}
                isLoading={isProductsLoading}
              />
            </Box>
          </Hide>
        )}
        <Stack
          className="product_content"
          rowGap="sm"
          gridColumn={{ base: '1/-1', lg: '9/-1' }}
          gridRow={{ base: 2, lg: 1 }}
          position={{ lg: 'sticky' }}
          top="2xl"
          alignSelf="start"
        >
          <ProductIntro
            title={productInfo.title}
            subtitle={productInfo.subtitle}
            longDescription={productInfo.description}
            productBullet={sanityProduct.productBullet}
            secondaryProductBullet={sanityProduct.secondaryProductBullet}
            isMoneyBackGuarantee={sanityProduct.isMoneyBackGuarantee}
            showReviews
            productSlugs={[slug]}
          />
          {!!bulletPoints?.length && (
            <Box>
              <RichTextResolver
                blocks={bulletPoints}
                container={ProductBulletPoints}
              />
            </Box>
          )}
          {isPromoCodeVisible &&
            isSelectedAvailable &&
            !!promoCodes?.length && (
              <PromoCode title={productInfo.title} promoCode={promoCodes[0]} />
            )}
          <Box className="product_configurator">{renderConfigurator()}</Box>
          <ProductCheckmarks
            productId={selectedShopifyProduct?.id}
            isShippingInfoVisible
            isTruemedVisible={false}
          />
          {disclaimer && <ProductDisclaimer>{disclaimer}</ProductDisclaimer>}
          {!!seeItInAction?.items?.length && (
            <ProductSeeItInAction key={slug} {...seeItInAction} />
          )}
        </Stack>
      </Grid.Inner>
    </Grid>
  );
};
